import { useEffect, type ReactNode } from "react";
import { AboutPage } from "../pages/AboutPage";
import { Navigate, Route, Routes, useSearchParams } from "react-router-dom";
import { Layout } from "../components/Layout";
import { AdminPage } from "../pages/AdminPage";
import { CartPage } from "../pages/CartPage";
import { CheckoutPage } from "../pages/CheckoutPage";
import { FAQPage } from "../pages/FAQPage";
import { InfoPage } from "../pages/InfoPage";
import { OrderDetailPage, OrdersPage, RepayOrderPage } from "../pages/OrdersPage";
import { ProductDetailPage } from "../pages/ProductDetailPage";
import { ProductsPage } from "../pages/ProductsPage";
import { ProfilePage } from "../pages/ProfilePage";
import { QuotePage } from "../pages/QuotePage";
import { HomePage } from "../pages/HomePage";
import { LegalPage } from "../pages/LegalPage";
import { useAppState } from "../state/AppState";
import { exchangeGoogleCode } from "../lib/api";

function AdminRoute({ children }: { children: ReactNode }) {
  const { isAdmin } = useAppState();
  return isAdmin ? <>{children}</> : <Navigate to="/" replace />;
}

export function App() {
  const { addToast, setUser } = useAppState();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const exchangeCode = searchParams.get("exchange_code");
    const error = searchParams.get("error");
    if (!exchangeCode && !error) return;

    if (error) {
      addToast("Google login failed. Please try again.", "error");
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete("error");
      setSearchParams(nextParams, { replace: true });
      return;
    }

    let cancelled = false;
    exchangeGoogleCode(exchangeCode!)
      .then((user) => {
        if (cancelled) return;
        setUser(user);
        addToast("Login successful", "success");
        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete("exchange_code");
        setSearchParams(nextParams, { replace: true });
      })
      .catch((exchangeError) => {
        if (cancelled) return;
        addToast(exchangeError instanceof Error ? exchangeError.message : "Google login failed.", "error");
        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete("exchange_code");
        setSearchParams(nextParams, { replace: true });
      });

    return () => {
      cancelled = true;
    };
  }, [addToast, searchParams, setSearchParams, setUser]);

  return (
    <Routes>
      <Route element={<Layout />}>
        <Route index element={<HomePage />} />
        <Route path="quote" element={<QuotePage />} />
        <Route path="products" element={<ProductsPage />} />
        <Route path="products/:productId" element={<ProductDetailPage />} />
        <Route path="cart" element={<CartPage />} />
        <Route path="checkout" element={<CheckoutPage />} />
        <Route path="orders" element={<OrdersPage />} />
        <Route path="orders/:orderId/pay" element={<RepayOrderPage />} />
        <Route path="orders/:orderId" element={<OrderDetailPage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="about" element={<AboutPage />} />
        <Route path="faq" element={<FAQPage />} />
        <Route path="privacypolicy" element={<LegalPage type="privacy" />} />
        <Route path="termsandconditions" element={<LegalPage type="terms" />} />
        <Route path="refundpolicy" element={<LegalPage type="refund" />} />
        <Route path="returnpolicy" element={<LegalPage type="returnpolicy" />} />
        <Route path="shippingpolicy" element={<LegalPage type="shipping" />} />
        <Route path="payment-return" element={<InfoPage type="payment" />} />
        <Route
          path="admin"
          element={
            <AdminRoute>
              <AdminPage section="overview" />
            </AdminRoute>
          }
        />
        <Route
          path="admin/orders/:orderId"
          element={
            <AdminRoute>
              <AdminPage />
            </AdminRoute>
          }
        />
        <Route
          path="admin/:section"
          element={
            <AdminRoute>
              <AdminPage />
            </AdminRoute>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
